import React, { useRef } from 'react';
import { AriaTextFieldOptions, useTextField } from '@react-aria/textfield';
import { useForceFocus } from '../Hooks/useForceFocus';
import TextfieldLabel from './TextfieldLabel';
import TextfieldHelperText from './TextfieldHelperText';
import TextfieldCharacterCount from './TextfieldCharacterCount';
import TextfieldResetButton from './TextfieldResetButton';
import { cn } from '../cn';
import { IconCopy } from '../Icons';

export type TextfieldProps = Omit<AriaTextFieldOptions<'input'> & {
  /**
   * Accepts a React component to be used as the preceding icon.
   */
  icon?: React.ReactNode,
  /**
   * Adds the character count component.
   */
  hasCount?: boolean,
  /**
   * Sets the maximum number of characters for the character count component.
   */
  maxCount?: number,
  /**
   * Applies a textarea element instead of an input.
   */
  multiline?: boolean,
  /**
   * Sets how many rows the textarea element will have.
   */
  rows?: number,
  /**
   * Applies a button to reset any entered text in the element.
   */
  hasReset?: boolean,
  /**
   * Applies a button to copy the text in the element.
   */
  hasCopyButton?: boolean,
  /**
   * Accepts a function that is fired when the reset button is clicked.
   */
  onReset?: () => void,
  innerRef?: React.MutableRefObject<HTMLInputElement>,
  focusOnMount?: boolean,
  /**
   * Function that is called when the base input element value changes.
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  className?: string;
  inputClassName?: string;
  after?: React.ReactNode;
}, 'errorMessage' & 'aria-errormessage'>;

export const TextField = ({
  label,
  description,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  icon,
  hasCount = false,
  maxCount = 0,
  multiline = false,
  rows,
  hasReset = false,
  hasCopyButton = false,
  onReset,
  innerRef,
  focusOnMount,
  inputClassName,
  className,
  after,
  placeholder,
  ...props
} : TextfieldProps): JSX.Element => {
  const ref = innerRef || useRef<HTMLInputElement>(null);

  const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField(
    {
      ...props,
      label,
      description,
      isRequired,
      isDisabled,
      isReadOnly,
      errorMessage,
    },
    ref
  );

  useForceFocus(ref, focusOnMount);

  return (
    <div className={cn(
      'flex flex-col', 
      isDisabled && [
        'opacity-40',
      ],
      className
    )}>
      {label && (<TextfieldLabel labelProps={labelProps} required={isRequired}>{label}</TextfieldLabel>)}
      <div className='flex flex-row' style={{ position: 'relative' }}>
        {icon && (
          <div
            className={cn(
              'inline-block absolute pl-2 text-input-text top-1/2 -translate-y-1/2',
              multiline && 'top-1.5 translate-y-0',
            )}
          >
            {icon}
          </div>
        )}
        {multiline ? <textarea
          {...(inputProps as any)}
          ref={ref}
          rows={rows}
          required={isRequired}
          disabled={isDisabled}
          placeholder={placeholder}
          className={cn([
            `py-[5px] ${!icon ? 'pl-3' : 'pl-9'} ${!(hasReset || hasCopyButton) ? 'pr-3' : 'pr-10'} w-full`,
            'bg-surface hover:bg-surface-dark',
            'text-text text-base font-semibold leading-6',
            'border-1 border-border hover:border-border-hover disabled:border-border',
            'focus:ring-2',
            inputClassName,
            isReadOnly && [
              'bg-surface-dark',
            ],
            errorMessage && [
              'border-error hover:border-error focus:border-error ',
            ],
            isDisabled && [
              'disabled:bg-surface',
              'cursor-not-allowed',
            ],
          ])}
        /> : <input
          {...inputProps}
          ref={ref}
          required={isRequired}
          disabled={isDisabled}
          placeholder={placeholder}
          className={cn([
            `py-[5px] ${!icon ? 'pl-3' : 'pl-9'} ${!(hasReset || hasCopyButton) ? 'pr-3' : 'pr-10'} w-full`,
            'bg-surface hover:bg-surface-dark',
            'text-text text-base font-semibold leading-6',
            'border-1 border-border hover:border-border-hover disabled:border-border',
            'focus:ring-2',
            isReadOnly && [
              'bg-surface-dark',
            ],
            errorMessage && [
              'border-error hover:border-error focus:border-error ',
            ],
            inputClassName,
            isDisabled && [
              'disabled:bg-surface',
              'cursor-not-allowed',
            ],
          ])}
        />}
        {after && <>{after}</>}
        {(hasReset) && (
          <div
            className={cn([
              'absolute top-1/2 right-0',
              'inline-block',
              '-translate-y-1/2 -translate-x-3',
              multiline && 'top-auto bottom-2 translate-y-0'
            ])}
          >
            <TextfieldResetButton onReset={onReset} />
          </div>)
        }
        {(hasCopyButton && !multiline) &&  
          <div className={cn(
            'absolute top-1/2 right-0',
            'inline-block',
            '-translate-y-1/2 -translate-x-3',
            'pointer-events-none',
            multiline && 'top-auto bottom-1.5 translate-y-0'
          )}>
            <IconCopy/>
          </div>
        }
      </div>
      {hasCount && (<TextfieldCharacterCount count={props.value ? props.value.length : 0} max={maxCount} />)}
      <div {
        ...((description && !errorMessage) ? descriptionProps
          : errorMessage ? errorMessageProps : '')
      }>
        {(errorMessage || description) ? (
          <TextfieldHelperText status={!errorMessage ? 'neutral' : 'error'}>
            {errorMessage instanceof Function ? errorMessage(null as any) : errorMessage || description}
          </TextfieldHelperText>
        ) : null}
      </div>
    </div>
  );
};
